/* RESPONSIBLE TEAM: team-standalone */
/* LIB FILE TAKEN FROM THE fin-zendesk-copilot repo for testing purposes */

import { setOwner } from '@ember/application';
import type ApplicationInstance from '@ember/application/instance';
import { inject as service } from '@ember/service';
import type RegionService from 'embercom/services/region-service';

// You will need to keep this list in sync with the standalone/copilot template in Rails
export enum FrameOutwardEvents {
  IC_FRAME_ADD_COMPOSER_CONTENT = 'IC_FRAME_ADD_COMPOSER_CONTENT',
  IC_FRAME_ADD_COMPOSER_PUBLIC_CONTENT = 'IC_FRAME_ADD_COMPOSER_PUBLIC_CONTENT',
  IC_FRAME_ADD_COMPOSER_PRIVATE_CONTENT = 'IC_FRAME_ADD_COMPOSER_PRIVATE_CONTENT',
  IC_FRAME_CLEAR_COMPOSER = 'IC_FRAME_CLEAR_COMPOSER',
  IC_FRAME_GET_CURRENT_COMPOSER_CONTENT = 'IC_FRAME_GET_CURRENT_COMPOSER_CONTENT',
  IC_FRAME_GET_SALESFORCE_COMPOSER_VISIBILITY_STATE = 'IC_FRAME_GET_SALESFORCE_COMPOSER_VISIBILITY_STATE',
}

export enum FrameInwardEvents {
  IC_FRAME_CONVERSATION_CHANGED = 'IC_FRAME_CONVERSATION_CHANGED',
  IC_FRAME_CURRENT_COMPOSER_CONTENT = 'IC_FRAME_CURRENT_COMPOSER_CONTENT',
  IC_FRAME_COLOR_SCHEME_CHANGED = 'IC_FRAME_COLOR_SCHEME_CHANGED',
  IC_FRAME_SALESFORCE_COMPOSER_VISIBILITY_STATE = 'IC_FRAME_SALESFORCE_COMPOSER_VISIBILITY_STATE',
}

type CopilotFrameEvent = FrameOutwardEvents | FrameInwardEvents;

const KNOWN_EVENTS = new Set([
  ...Object.values(FrameOutwardEvents),
  ...Object.values(FrameInwardEvents),
]);

export default class IntercomAdapter {
  @service declare regionService: RegionService;

  frameUrl = 'https://frame.intercom.com';
  listenWindow: Window;
  sendWindow: Window;
  handlers: Map<string, ((payload: any) => void)[]>;

  constructor({
    owner,
    listenWindow,
    sendWindow,
    developmentFrameUrl,
  }: {
    owner: ApplicationInstance;
    listenWindow: Window;
    sendWindow: Window;
    developmentFrameUrl?: string;
  }) {
    setOwner(this, owner);
    this.listenWindow = listenWindow;
    this.sendWindow = sendWindow;
    if (developmentFrameUrl) {
      this.frameUrl = developmentFrameUrl;
    } else {
      this.frameUrl = this.regionService.standaloneCopilotFrameBaseURL;
    }
    this.handlers = new Map();

    this.handleMessage = this.handleMessage.bind(this);
    this.listenWindow.addEventListener('message', this.handleMessage);
  }

  on(eventType: CopilotFrameEvent, handler: (payload: any) => void) {
    if (!KNOWN_EVENTS.has(eventType)) {
      console.warn(`Event type ${eventType} is not allowed`);
      return;
    }

    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, []);
    }
    this.handlers.get(eventType)!.push(handler);
  }

  off(eventType: CopilotFrameEvent, handler: (payload: any) => void) {
    if (!this.handlers.has(eventType)) {
      return;
    }
    let list = this.handlers.get(eventType)!;
    let index = list.indexOf(handler);
    if (index !== -1) {
      list.splice(index, 1);
    }
    if (list.length === 0) {
      this.handlers.delete(eventType);
    }
  }

  handleMessage(event: MessageEvent) {
    if (event.source !== this.sendWindow) {
      return;
    }

    let { type, payload } = event.data;

    if (this.handlers.has(type)) {
      this.handlers.get(type)!.forEach((handler) => handler(payload));
    }
  }

  destroy() {
    this.listenWindow.removeEventListener('message', this.handleMessage);
    this.handlers.clear();
  }

  async send(
    eventType: CopilotFrameEvent,
    payload: any,
    awaitEvent?: FrameInwardEvents.IC_FRAME_CURRENT_COMPOSER_CONTENT,
  ) {
    if (!KNOWN_EVENTS.has(eventType)) {
      console.warn(`Event type ${eventType} is not allowed`);
      return;
    }
    if (awaitEvent) {
      let promise = new Promise<
        | {
            text: string | null;
          }
        | boolean
      >((resolve) => {
        let once = (payload: any) => {
          this.off(awaitEvent, once);
          resolve(payload);
        };
        this.on(awaitEvent, once);
      });

      this.sendWindow.postMessage({ type: eventType, payload }, this.frameUrl);

      let result = await promise;

      return result;
    } else {
      this.sendWindow.postMessage({ type: eventType, payload }, this.frameUrl);
      return;
    }
  }
}
